/**
 * Carpet measurements — shared utility for the multi-size carpet feature.
 *
 * Customer / admin can add multiple carpets of different dimensions to the
 * same booking. New bookings persist `measurements.carpetItems[]`; legacy
 * bookings that pre-date this feature stored a single size in the flat
 * `carpetWidth` / `carpetLength` / `numberOfCarpets` fields. Every reader
 * (server pricing, client preview, emails, dashboards, invoices, ops
 * panel, executioner view) goes through these helpers so the two shapes
 * stay reconciled without each call site re-implementing the fallback.
 *
 * Why not migrate old data forward? See the chat that introduced this
 * feature: keeping the legacy shape readable on disk avoids a destructive
 * one-shot script and lets us roll back the UI cleanly if needed.
 */

export type MeasurementUnit = 'feet' | 'meters';

export interface CarpetItem {
  /** Optional room / note shown to the technician ("Living Room", "Master Bedroom"). */
  label?: string;
  /** In whatever `measurementUnit` the booking is using (feet or meters). */
  width: number;
  length: number;
  /** Count of carpets that share this exact size. Defaults to 1. */
  quantity: number;
}

/** Square-meters → square-feet (mirrors the constant in /api/bookings). */
export const SQM_TO_SQFT = 10.764;

export function getMeasurementUnit(meas: unknown): MeasurementUnit {
  const u = (meas as { measurementUnit?: string } | null | undefined)?.measurementUnit;
  return u === 'meters' ? 'meters' : 'feet';
}

/**
 * Read a measurement value from either the top-level shape (the customer
 * flow's `measurements` object) or the nested `serviceMeasurements` shape
 * used by the AI verification step. Mirrors the read pattern in
 * src/app/api/bookings/route.ts computeMeasurementCost.
 */
function readSm(meas: unknown): Record<string, unknown> {
  if (!meas || typeof meas !== 'object') return {};
  const m = meas as Record<string, unknown>;
  if (
    m.serviceMeasurements &&
    typeof m.serviceMeasurements === 'object' &&
    !Array.isArray(m.serviceMeasurements)
  ) {
    return m.serviceMeasurements as Record<string, unknown>;
  }
  return m;
}

/**
 * Returns a normalised `CarpetItem[]` from any supported `measurements`
 * shape. Three resolution paths:
 *   1. `carpetItems[]` present and non-empty → that array (validated entries only).
 *   2. Legacy `carpetWidth` / `carpetLength` / `numberOfCarpets` → wrap into one item.
 *   3. Nothing usable → empty array.
 *
 * Invalid rows (missing / zero / negative width|length, zero|negative quantity)
 * are filtered out so downstream callers can safely sum without guarding.
 */
export function normalizeCarpetItems(meas: unknown): CarpetItem[] {
  const sm = readSm(meas);
  const raw = sm.carpetItems;
  if (Array.isArray(raw) && raw.length > 0) {
    const out: CarpetItem[] = [];
    for (const r of raw) {
      if (!r || typeof r !== 'object') continue;
      const item = r as Record<string, unknown>;
      const width = Number(item.width) || 0;
      const length = Number(item.length) || 0;
      const quantity = Number(item.quantity) || 0;
      if (width <= 0 || length <= 0 || quantity <= 0) continue;
      out.push({
        label: typeof item.label === 'string' ? item.label : '',
        width,
        length,
        quantity,
      });
    }
    return out;
  }
  // Legacy single-size fallback — preserves pricing/display for bookings
  // saved before this feature shipped.
  const width = Number(sm.carpetWidth) || 0;
  const length = Number(sm.carpetLength) || 0;
  const quantity = Number(sm.numberOfCarpets) || 0;
  if (width > 0 && length > 0 && quantity > 0) {
    return [{ width, length, quantity }];
  }
  return [];
}

/** Total carpet count across all rows. Useful for "3 carpets" copy. */
export function carpetTotalCount(meas: unknown): number {
  return normalizeCarpetItems(meas).reduce((sum, c) => sum + c.quantity, 0);
}

/**
 * Total carpet area expressed in square FEET, regardless of the source
 * `measurementUnit`. The pricing model is sqft-based (basePrice = ₹/sqft)
 * so callers can multiply directly: `area * basePrice`.
 */
export function carpetTotalAreaSqFt(meas: unknown): number {
  const items = normalizeCarpetItems(meas);
  if (items.length === 0) return 0;
  const isMeters = getMeasurementUnit(meas) === 'meters';
  const raw = items.reduce((sum, c) => sum + c.width * c.length * c.quantity, 0);
  return isMeters ? raw * SQM_TO_SQFT : raw;
}

/**
 * Computes carpet line cost: round(totalSqFt × pricePerSqFt). Returns null
 * when there are no valid rows (so callers can fall back / show ₹0 cleanly).
 */
export function carpetLineCost(
  meas: unknown,
  pricePerSqFt: number
): number | null {
  if (!Number.isFinite(pricePerSqFt) || pricePerSqFt <= 0) return null;
  const items = normalizeCarpetItems(meas);
  if (items.length === 0) return null;
  return Math.round(carpetTotalAreaSqFt(meas) * pricePerSqFt);
}

/**
 * Human-readable measurement summary for the pricing sidebar, emails,
 * dashboards, and invoices.
 *
 *   - Empty:                          ""
 *   - Single row, no rate:            "8×10 ft × 2 carpets"
 *   - Single row, with rate:          "8×10 ft × 2 carpets × ₹70/sq.ft"
 *   - Multi rows, no rate:            "5 carpets across 3 sizes (228 sq.ft)"
 *   - Multi rows, with rate:          "5 carpets across 3 sizes (228 sq.ft × ₹70/sq.ft)"
 *
 * The total-area summary is intentionally in sq.ft (the pricing unit) so
 * the customer can reconcile the rupee figure to the displayed area.
 *
 * `pricePerSqft` is optional so display-only surfaces (emails, invoices,
 * dashboards) that don't have the live basePrice handy can still call
 * this without backfilling the rate. The booking flow sidebar passes it
 * so the line reads like the sofa breakdown ("2 seats × ₹1,100").
 */
export function carpetDescription(meas: unknown, pricePerSqft?: number): string {
  const items = normalizeCarpetItems(meas);
  if (items.length === 0) return '';
  const unit = getMeasurementUnit(meas);
  const unitLabel = unit === 'meters' ? 'm' : 'ft';
  const ratePart =
    pricePerSqft && pricePerSqft > 0
      ? ` × ₹${pricePerSqft.toLocaleString('en-IN')}/sq.ft`
      : '';
  if (items.length === 1) {
    const c = items[0];
    return `${c.width}×${c.length} ${unitLabel} × ${c.quantity} carpet${c.quantity !== 1 ? 's' : ''}${ratePart}`;
  }
  const totalCarpets = carpetTotalCount(meas);
  const totalSqFt = Math.round(carpetTotalAreaSqFt(meas));
  return `${totalCarpets} carpets across ${items.length} sizes (${totalSqFt} sq.ft${ratePart})`;
}

/**
 * Per-row description rendered under each itemised carpet line on the
 * sidebar / invoice / executioner job sheet. Format mirrors the single-row
 * case from carpetDescription so the visual rhythm is consistent.
 */
export function carpetItemDescription(item: CarpetItem, unit: MeasurementUnit): string {
  const unitLabel = unit === 'meters' ? 'm' : 'ft';
  const base = `${item.width}×${item.length} ${unitLabel} × ${item.quantity}`;
  return item.label ? `${item.label} — ${base}` : base;
}

/** One auditable carpet line: its dimensions, derived area, and rupee cost. */
export interface CarpetLineItem {
  label?: string;
  width: number;
  length: number;
  quantity: number;
  /** 'ft' | 'm' — the booking's measurement unit, for the dimension label. */
  unitLabel: 'ft' | 'm';
  /** Area for THIS row in square feet (the pricing unit), rounded. */
  areaSqFt: number;
  /** Rupee cost for THIS row. The rows sum EXACTLY to carpetLineCost — any
   *  rounding remainder is absorbed into the last row so the itemised lines
   *  always reconcile to the displayed total (auditable by construction). */
  amount: number;
}

/**
 * Explodes a multi-size carpet measurement into per-row line items so the
 * pricing breakdown can show each carpet's `dims → area × rate = amount`
 * instead of a single collapsed "N carpets across M sizes" summary. Returns
 * [] when there are no usable rows or no positive rate.
 *
 * The per-row amounts are guaranteed to sum to `carpetLineCost(meas, rate)`
 * — the last row absorbs any rounding drift (only possible for metre inputs,
 * since feet areas are integers) — so an auditor adding the lines always
 * lands on the headline total.
 */
export function carpetLineItems(
  meas: unknown,
  pricePerSqFt: number,
): CarpetLineItem[] {
  if (!Number.isFinite(pricePerSqFt) || pricePerSqFt <= 0) return [];
  const items = normalizeCarpetItems(meas);
  if (items.length === 0) return [];
  const isMeters = getMeasurementUnit(meas) === 'meters';
  const unitLabel: 'ft' | 'm' = isMeters ? 'm' : 'ft';

  const rows = items.map((c) => {
    const rawArea = c.width * c.length * c.quantity;
    const areaSqFtExact = isMeters ? rawArea * SQM_TO_SQFT : rawArea;
    return {
      label: c.label,
      width: c.width,
      length: c.length,
      quantity: c.quantity,
      unitLabel,
      areaSqFt: Math.round(areaSqFtExact),
      amount: Math.round(areaSqFtExact * pricePerSqFt),
    };
  });

  // Reconcile the per-row rupee amounts to the authoritative line cost so
  // the itemised lines always sum to the headline total.
  const authoritative = carpetLineCost(meas, pricePerSqFt) ?? 0;
  const summed = rows.reduce((s, r) => s + r.amount, 0);
  const drift = authoritative - summed;
  if (drift !== 0) {
    rows[rows.length - 1].amount += drift;
  }
  return rows;
}
